import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CaseReturn, CaseStatus, HomeReturn } from '../clients/rust-api-client';
import { billingService, homeService } from '../clients/ApiClients';
import { Badge } from '../common/Catalyst/badge';
import { Button } from '../common/Catalyst/button';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from '../common/Catalyst/dialog';
import {
  Description,
  ErrorMessage,
  Field,
  FieldGroup,
  Fieldset,
  Label,
} from '../common/Catalyst/fieldset';
import { Switch } from '../common/Catalyst/switch';
import { Textarea } from '../common/Catalyst/textarea';
import { FieldPicker } from '../common/Custom/FieldPicker';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../common/Catalyst/table';
import { Subheading } from '../common/Catalyst/heading';
import { Text } from '../common/Catalyst/text';
import LoadingSpinner from '../LoadingSpinner';
import ShowAlert from '../ShowAlert';
import {
  billingErrorMessage,
  formatBillingDate,
  formatMoney,
} from '../utils/billingHelpers';
import { caseStatus } from './enumLabels';

interface Props {
  hoaId: string;
  canOpen: boolean;
  canAdvance: boolean;
}

const PER_PAGE = 25;

/**
 * Collections cases for an association.
 *
 * **The aging job opens cases and takes them as far as a late notice.**
 * Demand, attorney, lien and foreclosure carry legal consequences and
 * are decisions a person makes — which is why advancing is Owner/Admin
 * while opening one and writing a note are not. Resolution is the other
 * way round: paying up closes a case by itself.
 */
const CollectionsPanel: React.FC<Props> = ({ hoaId, canOpen, canAdvance }) => {
  const navigate = useNavigate();
  const [cases, setCases] = useState<CaseReturn[]>([]);
  const [homes, setHomes] = useState<HomeReturn[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [includeClosed, setIncludeClosed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [opening, setOpening] = useState(false);
  const [homeId, setHomeId] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  // The server's refusal shows above the form; a missing home is the
  // field's own problem and shows under it.
  const [openError, setOpenError] = useState('');
  const [homeError, setHomeError] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [list, homeList] = await Promise.all([
        billingService.listCases({
          hoaId,
          page,
          perPage: PER_PAGE,
          includeClosed,
        }),
        homeService.getHomesByHoa({ hoaId }),
      ]);
      setCases(list.data.cases);
      setTotal(list.data.total);
      setHomes(homeList.data);
    } catch (err) {
      console.error('Error loading cases:', err);
      setError(billingErrorMessage(err, "Couldn't load collections."));
    } finally {
      setLoading(false);
    }
  }, [hoaId, page, includeClosed]);

  useEffect(() => {
    void load();
  }, [load]);

  const open = async (): Promise<void> => {
    if (!homeId) {
      setHomeError('Pick the home this case is about.');
      return;
    }
    try {
      setBusy(true);
      setOpenError('');
      const { data } = await billingService.openCase({
        hoaId,
        caseOpenRequest: { home_id: homeId, reason: reason.trim() || null },
      });
      setOpening(false);
      setHomeId('');
      setReason('');
      navigate(`/hoa/${hoaId}/billing/collections/${data.id}`);
    } catch (err) {
      console.error('Error opening case:', err);
      // A 409 here names the case that is already open, so it is more
      // useful shown than translated.
      setOpenError(billingErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const lastPage = Math.max(1, Math.ceil(total / PER_PAGE));

  if (loading && cases.length === 0) return <LoadingSpinner />;

  return (
    <>
      {error && (
        <div className="mb-4">
          <ShowAlert
            alertType="error"
            alertTitle="Couldn't load collections"
            alertMessage={error}
            isVisible={!!error}
            onClose={() => setError(null)}
            timeout={10000}
            isInline
          />
        </div>
      )}

      <div className="mb-4 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <Subheading>Collections</Subheading>
          <Text className="mt-1">
            The aging job opens a case and takes it as far as a late notice.
            Demand, attorney, lien and foreclosure carry legal consequences and
            are decisions a person makes. Paying up closes a case by itself.
          </Text>
        </div>
        {canOpen && (
          <Button color="sky" onClick={() => setOpening(true)}>
            Open a case
          </Button>
        )}
      </div>

      <div className="mb-4 flex items-center gap-3">
        <Switch
          checked={includeClosed}
          onChange={(v) => {
            setPage(1);
            setIncludeClosed(v);
          }}
          aria-label="Include closed cases"
        />
        <Text className="text-sm">Include closed cases</Text>
      </div>

      {cases.length === 0 ? (
        <div className="rounded-lg border border-dashed border-zinc-300 dark:border-zinc-700 p-6">
          <Text>
            {includeClosed
              ? 'No collections case has ever been opened for this association.'
              : 'No open collections cases. Nothing here is past this association’s threshold.'}
          </Text>
        </div>
      ) : (
        <>
          <Table dense>
            <TableHead>
              <TableRow>
                <TableHeader>Home</TableHeader>
                <TableHeader>Payer</TableHeader>
                <TableHeader>Opened</TableHeader>
                <TableHeader className="text-right">At</TableHeader>
                <TableHeader className="text-right">Days</TableHeader>
                <TableHeader>Status</TableHeader>
                <TableHeader> </TableHeader>
              </TableRow>
            </TableHead>
            <TableBody>
              {cases.map((c) => (
                <TableRow key={c.id}>
                  <TableCell>{c.home_label ?? c.home_id}</TableCell>
                  <TableCell>{c.payer_name ?? '—'}</TableCell>
                  <TableCell>{formatBillingDate(c.opened_on)}</TableCell>
                  <TableCell className="text-right">
                    {formatMoney(c.trigger_balance)}
                  </TableCell>
                  <TableCell className="text-right">
                    {c.trigger_days_past_due ?? '—'}
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap items-center gap-1">
                      <Badge color={caseStatus.color(c.status)}>
                        {caseStatus.label(c.status)}
                      </Badge>
                      {c.closed_on && <Badge color="zinc">Closed</Badge>}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Button
                      plain
                      onClick={() =>
                        navigate(`/hoa/${hoaId}/billing/collections/${c.id}`)
                      }
                    >
                      Open
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <div className="mt-4 flex items-center justify-between">
            <Text>
              {total} case{total === 1 ? '' : 's'} · page {page} of {lastPage}
            </Text>
            <div className="flex gap-2">
              <Button
                plain
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
              >
                Previous
              </Button>
              <Button
                plain
                disabled={page >= lastPage}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        </>
      )}

      {!canAdvance && cases.length > 0 && (
        <Text className="mt-4 text-sm">
          You can open a case and record what was said. Taking one past a late
          notice is limited to an Owner or Admin.
        </Text>
      )}

      <Dialog open={opening} onClose={() => setOpening(false)} size="xl">
        <DialogTitle>Open a collections case</DialogTitle>
        <DialogBody>
          {openError && (
            <div className="mb-4">
              <ShowAlert
                alertType="error"
                alertTitle="The case was not opened"
                alertMessage={openError}
                isVisible
                onClose={() => setOpenError('')}
                timeout={0}
                isInline
              />
            </div>
          )}
          <Text className="mb-4">
            A case opened by hand starts at <strong>reminder</strong>, exactly
            where the aging job would start it — so this is an observation that
            a home needs working, not an escalation. Its balance and days past
            due are read from the ledger rather than typed.
          </Text>
          <Fieldset>
            <FieldGroup>
              <Field>
                <Label>Home</Label>
                <FieldPicker
                  label="Home"
                  placeholder="Choose a home…"
                  value={homeId}
                  onChange={(v) => {
                    setHomeId(v);
                    setHomeError('');
                    setOpenError('');
                  }}
                  options={homes.map((h) => ({
                    value: h.id,
                    label: [h.street, h.street2].filter(Boolean).join(' '),
                  }))}
                />
                {homeError && <ErrorMessage>{homeError}</ErrorMessage>}
              </Field>
              <Field>
                <Label>Note</Label>
                <Description>
                  Optional. Recorded as the first entry on the case&rsquo;s
                  trail.
                </Description>
                <Textarea
                  rows={2}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                />
              </Field>
            </FieldGroup>
          </Fieldset>
        </DialogBody>
        <DialogActions>
          <Button plain onClick={() => setOpening(false)} disabled={busy}>
            Cancel
          </Button>
          <Button color="sky" onClick={open} disabled={busy}>
            {busy ? 'Opening…' : 'Open case'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CollectionsPanel;

/** Advance a case to a new status. Owner/Admin. */
export const AdvanceCaseDialog: React.FC<{
  caseRow: CaseReturn | null;
  onClose: () => void;
  onDone: () => void;
}> = ({ caseRow, onClose, onDone }) => {
  const [status, setStatus] = useState<CaseStatus>('demand');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    setStatus('demand');
    setReason('');
    setErrors({});
  }, [caseRow]);

  const submit = async (): Promise<void> => {
    if (!reason.trim()) {
      setErrors({ reason: 'A reason is required — it goes on the trail.' });
      return;
    }
    if (!caseRow) return;
    try {
      setBusy(true);
      await billingService.advanceCase({
        caseId: caseRow.id,
        caseAdvanceRequest: { status, reason: reason.trim() },
      });
      onDone();
      onClose();
    } catch (err) {
      console.error('Error advancing case:', err);
      setErrors({ form: billingErrorMessage(err) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!caseRow} onClose={onClose} size="xl">
      <DialogTitle>Move this case on</DialogTitle>
      <DialogBody>
        {errors.form && (
          <div className="mb-4">
            <ShowAlert
              alertType="error"
              alertTitle="The case was not moved"
              alertMessage={errors.form}
              isVisible
              onClose={() => setErrors({})}
              timeout={0}
              isInline
            />
          </div>
        )}
        <Fieldset>
          <FieldGroup>
            <Field>
              <Label>To</Label>
              <Description>
                Choosing <strong>Resolved</strong> closes the case. It also
                happens by itself when the account is paid up, so closing by
                hand is for the other reasons — a written-off debt, a case
                opened in error.
              </Description>
              <FieldPicker
                label="To"
                value={status}
                onChange={(v) => setStatus(v as CaseStatus)}
                options={caseStatus.options().map((o) => ({
                  value: o.value,
                  label: o.label,
                }))}
              />
              {caseStatus.help(status) && (
                <Description className="mt-1">
                  {caseStatus.help(status)}
                </Description>
              )}
            </Field>
            <Field>
              <Label>Reason</Label>
              <Description>
                Permanent, and it is what the trail will show alongside the
                status change.
              </Description>
              <Textarea
                rows={3}
                value={reason}
                invalid={!!errors.reason}
                onChange={(e) => {
                  setReason(e.target.value);
                  setErrors({});
                }}
              />
              {errors.reason && <ErrorMessage>{errors.reason}</ErrorMessage>}
            </Field>
          </FieldGroup>
        </Fieldset>
      </DialogBody>
      <DialogActions>
        <Button plain onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button color="red" onClick={submit} disabled={busy}>
          {busy ? 'Moving…' : `Move to ${caseStatus.label(status)}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
